import { EyeIcon, UnlinkIcon, WriteIcon } from "@xola/icons";
import { Dot, Popover, PopoverList, Spinner, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, DemographicIcon, formatDate, getWaiverParticipantCount, SecondaryButton } from "common";
import { noop } from "lodash";
import throttle from "p-throttle";
import React, { ReactNode, useState } from "react";
import { Guest, PurchaseItem, Waiver, WaiverParticipant } from "types";
import { api } from "../../../api/api";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useModal } from "../../../hooks/useVisibility";
import { LinkWaiverModal } from "./LinkWaiverModal";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { getAppendRosterTarget } from "@/components/Dashboard/Roster/RosterGuestList/WaiverCell";
import { getLinkedGuest, useIsParticipantMinor } from "@/models/Waiver";
import { canModifyWaivers } from "@/stores/globalStore";

interface LinkedWaiverPopoverProps {
    purchaseItem: PurchaseItem;
    guests: Guest[];
    appendTo?: ReactNode;
}

export const LinkedWaiverPopover = ({ guests, purchaseItem, appendTo }: LinkedWaiverPopoverProps) => {
    const waiverPopover = useModal();

    const waiverParticipantCount = getWaiverParticipantCount(purchaseItem.waivers);

    return (
        <div className="px-1">
            <WaiverButton
                waiverParticipantCount={waiverParticipantCount}
                totalQuantity={purchaseItem.quantity}
                onClick={waiverPopover.toggle}
            />
            {waiverPopover.isVisible && (
                <LinkWaiver
                    waiverPopover={waiverPopover}
                    purchaseItem={purchaseItem}
                    guests={guests}
                    appendTo={getAppendRosterTarget() ?? appendTo}
                />
            )}
        </div>
    );
};

interface LinkWaiverProps {
    waiverPopover: ReturnType<typeof useModal>;
    purchaseItem: PurchaseItem;
    guests: Guest[];
    appendTo?: ReactNode;
}

const LinkWaiver = ({ waiverPopover, purchaseItem, guests, appendTo }: LinkWaiverProps) => {
    const linkPurchaseWaiverModal = useModal();

    const linkNewPurchaseWaiver = () => {
        linkPurchaseWaiverModal.toggle();
    };

    const handleWaiverModalClose = () => {
        linkPurchaseWaiverModal.hide();
        waiverPopover.hide();
    };

    return (
        <>
            {waiverPopover.isVisible && (
                <WaiverPopover
                    visible={waiverPopover.isVisible && !linkPurchaseWaiverModal.isVisible}
                    purchaseItem={purchaseItem}
                    guests={guests}
                    appendTo={appendTo}
                    onLinkNewWaiver={linkNewPurchaseWaiver}
                    onClickOutside={waiverPopover.hide}
                />
            )}

            {linkPurchaseWaiverModal.isVisible && (
                <LinkWaiverModal
                    isOpen={linkPurchaseWaiverModal.isVisible}
                    purchaseItem={purchaseItem}
                    onClose={handleWaiverModalClose}
                />
            )}
        </>
    );
};

interface WaiverButtonProps {
    waiverParticipantCount: number;
    totalQuantity: number;
    onClick: () => void;
}

const WaiverButton = ({ waiverParticipantCount, totalQuantity, onClick }: WaiverButtonProps) => {
    let waiverDotColor = "danger";
    if (waiverParticipantCount > 0) {
        waiverDotColor = waiverParticipantCount >= totalQuantity ? "success" : "warning";
    }
    const showLinkWaiver = canModifyWaivers();

    return (
        <Tooltip content="Waivers" disabled={!showLinkWaiver} appendTo={document.body}>
            <SecondaryButton
                size="small"
                className={clsx(!showLinkWaiver && "!cursor-not-allowed", "min-w-20")}
                onClick={onClick}
            >
                <span className="flex items-center">
                    <Dot className="!ml-0 mr-0.5" color={waiverDotColor} />
                    <WriteIcon className="mt-0.5" />
                </span>

                <div className="pl-2 font-bold" data-testid="split-pay-attendees-waivers-count">
                    {waiverParticipantCount}/{totalQuantity}
                </div>
            </SecondaryButton>
        </Tooltip>
    );
};

interface WaiverPopoverProps {
    visible: boolean;
    purchaseItem: PurchaseItem;
    guests: Guest[];
    appendTo?: ReactNode;
    onLinkNewWaiver: () => void;
    onClickOutside?: () => void;
}

const unLinkThrottle = throttle({ limit: 1, interval: 200 })(async (id: ID, purchaseId: ID) => {
    await api.put(`/waivers/${id}`, {
        purchase: purchaseId,
        remove: true,
    });
});

const WaiverPopover = ({
    visible,
    purchaseItem,
    guests,
    appendTo,
    onLinkNewWaiver,
    onClickOutside,
}: WaiverPopoverProps) => {
    const invalidate = useInvalidate();
    const linkedPurchaseWaivers = purchaseItem.waivers ?? [];
    const [isUnlinking, setIsUnlinking] = useState(false);

    const handleRemoveWaiver = async (waiver: Waiver) => {
        const purchase = purchaseItem.purchase;
        if (purchase) {
            setIsUnlinking(true);
            await unLinkThrottle(waiver.id, purchase?.id);
            invalidate("waivers", waiver.id);
            invalidate("purchases", purchase.id);
            invalidate("timeslots");
            invalidate("events");
            setIsUnlinking(false);
        }
    };

    const showLinkWaiver = canModifyWaivers();

    return (
        <Popover
            visible={visible && showLinkWaiver}
            zIndex={30}
            offset={[0, 18]}
            className="w-75 overflow-x-visible border-b-transparent pt-4"
            appendTo={appendTo}
            onClickOutside={onClickOutside}
        >
            <Popover.Content>
                <div className="max-h-96 overflow-y-scroll">
                    {linkedPurchaseWaivers?.map((waiver: Waiver) =>
                        waiver.participants?.map((participant) => (
                            <PopoverList.Item
                                key={participant.id}
                                name={participant.id}
                                className="!border-white py-1 font-medium"
                                onClickItem={noop}
                            >
                                <WaiverParticipantItem
                                    waiver={waiver}
                                    isLinked={getLinkedGuest(guests, participant.id) !== undefined}
                                    participant={participant}
                                    isLoading={isUnlinking}
                                    onRemoveWaiver={handleRemoveWaiver}
                                />
                            </PopoverList.Item>
                        )),
                    )}
                </div>

                {showLinkWaiver && (
                    <PopoverList.Item name="linkWaiver" className="!border-white bg-white pt-4 pb-4" onClickItem={noop}>
                        <SecondaryButton className="w-full" onClick={onLinkNewWaiver}>
                            Link Waiver
                        </SecondaryButton>
                    </PopoverList.Item>
                )}
            </Popover.Content>
        </Popover>
    );
};

interface WaiverParticipantItemProps {
    waiver: Waiver;
    isLinked: boolean;
    isLoading?: boolean;
    participant: WaiverParticipant;
    onRemoveWaiver: (waiver: Waiver) => void;
}

const WaiverParticipantItem = ({
    waiver,
    isLinked,
    isLoading = false,
    participant,
    onRemoveWaiver,
}: WaiverParticipantItemProps) => {
    const isMinor = useIsParticipantMinor(participant, waiver.waiverTypeId);

    const handleRemoveItemClick = () => {
        onRemoveWaiver(waiver);
    };

    return (
        <>
            <span className="mr-auto flex items-center space-x-1">
                <Dot size="small" color={isLinked ? "success" : "danger"} />
                <Tooltip
                    content={isMinor ? `Minor, DOB: ${formatDate(participant.dateOfBirth, DateFormat.DATE)}` : ""}
                    disabled={!isMinor}
                    appendTo={document.body}
                >
                    <span className="flex items-center">
                        <DemographicIcon name={isMinor ? "child" : "adult"} size="tiny" />
                        {participant.customerName}
                    </span>
                </Tooltip>
            </span>

            <div className="flex items-center space-x-1">
                <InlineConfirmDeletePopover
                    placement="auto"
                    confirmButtonText="Confirm"
                    onConfirm={handleRemoveItemClick}
                >
                    <Tooltip content="Unlink waiver" delay={1000} appendTo={document.body}>
                        <SecondaryButton disabled={isLoading} size="tiny">
                            {isLoading ? <Spinner size="tiny" className="h-[13px]" /> : <UnlinkIcon size="tiny" />}
                        </SecondaryButton>
                    </Tooltip>
                </InlineConfirmDeletePopover>

                <Tooltip content="Preview Waiver" appendTo={document.body}>
                    <SecondaryButton size="tiny" onClick={() => window.open(waiver.url, "_blank")}>
                        <EyeIcon size="tiny" />
                    </SecondaryButton>
                </Tooltip>
            </div>
        </>
    );
};
